import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Copy, Check, Plus, Share2, Ban, ExternalLink, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";

const LINK_STATUS = {
  SENT: { label: "Sent", desc: "Created — not opened yet", style: "border-ivory/30 text-ivory/80" },
  OPENED: { label: "Opened", desc: "Recipient clicked the link", style: "border-sky-400/40 text-sky-300" },
  COMPLETED: { label: "Subscribed", desc: "Application submitted", style: "border-emerald-400/50 text-emerald-300" },
  EXPIRED: { label: "Ignored", desc: "7 days passed without application", style: "border-amber-400/40 text-amber-300" },
  REVOKED: { label: "Revoked", desc: "Disabled by you", style: "border-red-400/40 text-red-300" },
};

const linkUrl = (code) => `${window.location.origin}/candidatura/${code}`;

const waMessage = (code, label) =>
  `VillaFlow Private Collection\n\n${label ? `${label} — you` : "You"} have been personally invited to present your property for selection by the VillaFlow Selection Team.\n\nYour private application link (valid for 7 days):\n${linkUrl(code)}\n\nEvery Stay. More Value.`;

function fmtLeft(seconds) {
  if (seconds == null) return "—";
  const d = Math.floor(seconds / 86400);
  const h = Math.floor((seconds % 86400) / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (d > 0) return `${d}d ${h}h left`;
  if (h > 0) return `${h}h ${m}m left`;
  return `${m}m left`;
}

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
  } catch {
    return iso;
  }
}

export default function ShareLinks() {
  const [links, setLinks] = useState(null);
  const [label, setLabel] = useState("");
  const [creating, setCreating] = useState(false);
  const [copied, setCopied] = useState("");

  const load = () => api.get("/admin/share-links").then((r) => setLinks(r.data.links)).catch(() => setLinks([]));
  useEffect(() => { load(); }, []);

  const create = async () => {
    setCreating(true);
    try {
      await api.post("/admin/share-links", { label: label.trim() });
      toast.success("Private link created — valid for 7 days");
      setLabel("");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setCreating(false);
    }
  };

  const copy = async (code) => {
    try {
      await navigator.clipboard.writeText(linkUrl(code));
      setCopied(code);
      toast.success("Link copied");
      setTimeout(() => setCopied(""), 1500);
    } catch {
      toast.error("Copy failed");
    }
  };

  const revoke = async (l) => {
    try {
      await api.post(`/admin/share-links/${l.id}/revoke`);
      toast.success(`Link ${l.code} revoked`);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  return (
    <div data-testid="admin-share-links">
      <div className="flex items-end justify-between mb-8">
        <div>
          <p className="text-[11px] tracking-[0.3em] uppercase text-gold">WhatsApp Outreach</p>
          <h1 className="mt-3 font-serif font-light text-4xl text-ivory">Share Links</h1>
          <p className="mt-2 text-ivory/45 text-sm font-light max-w-xl">Single-use private application links. Each link expires automatically after 7 days.</p>
        </div>
      </div>

      {/* Create */}
      <div className="border border-gold/25 bg-navy-800 p-6 mb-10 flex flex-col sm:flex-row gap-4 sm:items-end" data-testid="share-link-form">
        <label className="block flex-1">
          <span className="block text-[10px] tracking-widest uppercase text-ivory/45 mb-2">Label — owner or villa name (optional)</span>
          <input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="e.g. Villa Rosa — Mr. Bianchi" className="w-full bg-transparent border-b border-ivory/20 focus:border-gold outline-none py-2 text-ivory text-sm placeholder:text-ivory/25" data-testid="share-link-label-input" />
        </label>
        <button onClick={create} disabled={creating} className="inline-flex items-center justify-center gap-2 bg-ivory hover:bg-gold text-navy px-8 py-3 text-[10px] tracking-[0.2em] uppercase transition-colors disabled:opacity-50" data-testid="create-share-link-btn">
          {creating ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />} Create Link
        </button>
      </div>

      {/* Legend */}
      <div className="mb-10" data-testid="share-links-legend">
        <p className="text-[10px] tracking-[0.3em] uppercase text-ivory/40 mb-4">Status Legend</p>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {Object.entries(LINK_STATUS).map(([key, s]) => (
            <div key={key} className="border border-ivory/10 bg-navy-800 px-4 py-3" data-testid={`legend-${key.toLowerCase()}`}>
              <span className={`inline-flex items-center px-3 py-1 border text-[10px] tracking-[0.18em] uppercase ${s.style}`}>{s.label}</span>
              <p className="mt-2 text-[11px] text-ivory/45 font-light leading-snug">{s.desc}</p>
            </div>
          ))}
        </div>
      </div>

      {/* List */}
      {links === null ? (
        <p className="text-ivory/40">Loading…</p>
      ) : links.length === 0 ? (
        <div className="border border-ivory/10 bg-navy-800 p-16 text-center" data-testid="share-links-empty">
          <p className="font-serif italic text-2xl text-ivory/60">No share links yet.</p>
          <p className="mt-3 text-ivory/40 text-sm font-light">Create your first link and share it on WhatsApp with a villa owner.</p>
        </div>
      ) : (
        <div className="border border-ivory/10 divide-y divide-ivory/10" data-testid="share-links-list">
          {links.map((l) => {
            const s = LINK_STATUS[l.status] || LINK_STATUS.SENT;
            const active = l.status === "SENT" || l.status === "OPENED";
            return (
              <div key={l.id} className="px-5 py-4 bg-navy-800" data-testid={`share-link-row-${l.code}`}>
                <div className="grid grid-cols-12 gap-4 items-center">
                  <div className="col-span-12 md:col-span-3">
                    <p className="text-ivory text-sm font-light truncate" data-testid={`share-link-label-${l.code}`}>{l.label || "—"}</p>
                    <p className="font-mono text-gold/80 tracking-wider text-xs mt-1">{l.code}</p>
                  </div>
                  <div className="col-span-6 md:col-span-2">
                    <span className={`inline-flex items-center px-3 py-1 border text-[10px] tracking-[0.18em] uppercase ${s.style}`} data-testid={`share-link-status-${l.code}`}>{s.label}</span>
                  </div>
                  <div className="col-span-6 md:col-span-3 text-[11px] text-ivory/50 font-light space-y-0.5">
                    <p>Created {fmtDate(l.created_at)}</p>
                    <p>{active ? fmtLeft(l.remaining_seconds) : l.status === "COMPLETED" ? `Submitted ${fmtDate(l.used_at)}` : l.status === "EXPIRED" ? `Expired ${fmtDate(l.expires_at)}` : `Expires ${fmtDate(l.expires_at)}`}</p>
                    {l.opened_at && <p>Opened {fmtDate(l.opened_at)}{l.opened_count > 1 ? ` · ${l.opened_count}×` : ""}</p>}
                  </div>
                  <div className="col-span-12 md:col-span-4 flex flex-wrap items-center gap-2 md:justify-end">
                    <button onClick={() => copy(l.code)} className="inline-flex items-center gap-1.5 border border-ivory/20 hover:border-gold text-ivory/60 hover:text-gold px-3 py-2 text-[10px] tracking-widest uppercase transition-colors" data-testid={`copy-${l.code}`}>
                      {copied === l.code ? <Check size={12} /> : <Copy size={12} />} Copy
                    </button>
                    {active && (
                      <a href={`https://wa.me/?text=${encodeURIComponent(waMessage(l.code, l.label))}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 border border-emerald-400/40 hover:border-emerald-300 text-emerald-300 px-3 py-2 text-[10px] tracking-widest uppercase transition-colors" data-testid={`whatsapp-${l.code}`}>
                        <Share2 size={12} /> WhatsApp
                      </a>
                    )}
                    {l.status === "COMPLETED" && l.application_id && (
                      <Link to={`/admin/applications/${l.application_id}`} className="inline-flex items-center gap-1.5 border border-gold/40 text-gold px-3 py-2 text-[10px] tracking-widest uppercase hover:bg-gold/10 transition-colors" data-testid={`view-application-${l.code}`}>
                        <ExternalLink size={12} /> Application
                      </Link>
                    )}
                    {active && (
                      <button onClick={() => revoke(l)} className="inline-flex items-center gap-1.5 border border-red-400/30 text-red-300/80 hover:text-red-300 hover:border-red-400/60 px-3 py-2 text-[10px] tracking-widest uppercase transition-colors" data-testid={`revoke-${l.code}`}>
                        <Ban size={12} /> Revoke
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
